import React, { useState } from 'react';
import { Paper, Tabs, Tab, TabPanel } from '@kambi/spark';

import classes from '../../../App.module.css';
import CodeBlock from 'components/layout/code-block/CodeBlock';
import {
    BetOfferScorer,
    BetOfferYesNo,
    BetOfferThreeWayHcp,
    BetOfferMultiPosition,
    BetOfferDefault,
} from './jsonExamples';

const BetOfferLabelTabs = () => {
    const [value, setValue] = useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <div className={classes.Tabs}>
            <Paper>
                <Tabs
                    kind="inverted"
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Scorer" />
                    <Tab label="Yes/No" />
                    <Tab label="3-Way Handicap" />
                    <Tab label="Multi Position" />
                    <Tab label="Default" />
                </Tabs>
            </Paper>
            <TabPanel index={0} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Scorer - id: 17</b>.
                </p>
                <p>
                    <i>
                        If the outcome from the betOffer has criterion it will
                        append the criterion name after the betOffer criterion
                        label.
                    </i>
                </p>
                <CodeBlock
                    title={BetOfferScorer.title}
                    expectedOutput={BetOfferScorer.expectedOutput}
                    initialDisplayedCode={BetOfferScorer.initialDisplayedCode}
                    moreCode={BetOfferScorer.moreCode}
                />
            </TabPanel>
            <TabPanel index={1} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> YES/NO - id: 18</b>.
                </p>
                <p>
                    <i>
                        If the outcome from the betOffer has participant it will
                        append the participant after the betOffer criterion
                        label.
                    </i>
                </p>
                <CodeBlock
                    title={BetOfferYesNo.title}
                    expectedOutput={BetOfferYesNo.expectedOutput}
                    initialDisplayedCode={BetOfferYesNo.initialDisplayedCode}
                    moreCode={BetOfferYesNo.moreCode}
                />
            </TabPanel>
            <TabPanel index={2} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> 3-Way Handicap - id: 11</b>.
                </p>
                <p>
                    <i>
                        It will append the outcome line after the bet offer
                        criterion label. If the event has the tag 'AWAY_HOME' it
                        will append "{`Starts 0-{outcomeLine}`}", and otherwise
                        the default appended will be "{`Starts {outcomeLine}-0`}
                        ".
                    </i>
                </p>
                <p style={{ marginBottom: '0' }}>
                    <i>
                        It accepts a callback for translations, used to
                        translate a static text and the following{' '}
                        <b>key needs to be added</b> to your own translations:
                    </i>
                </p>
                <ul>
                    <li>
                        <i>"betoffer.threewayhandicap.lineHeader" : "Starts"</i>
                    </li>
                </ul>
                <p>
                    <i>
                        If the callback isn't provided it will fallback to the
                        english text.
                    </i>
                </p>
                <CodeBlock
                    title={BetOfferThreeWayHcp.title}
                    expectedOutput={BetOfferThreeWayHcp.expectedOutput}
                    initialDisplayedCode={
                        BetOfferThreeWayHcp.initialDisplayedCode
                    }
                    moreCode={BetOfferThreeWayHcp.moreCode}
                />
            </TabPanel>
            <TabPanel index={3} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Multiposition - id:19</b> - is specific only to horse
                    racing and greyhounds betOffer, for the Forecast/Tricast
                    outcomes.
                </p>
                <p>
                    <i>
                        In order to determine the correct label an object
                        "selections" is <b>required</b>, in the the following
                        format: {`{eventId}_{participantId}: {position}`} -
                        where position can be: "FIRST", "SECOND", "THIRD",
                        "ANY".
                    </i>
                </p>
                <p>
                    <i>
                        The offering API usually provides two types of betoffers
                        for multiposiion. One with the property placeLimit = 2
                        and one with placeLimit = 3. For a selections object
                        with 2 non-"ANY" (either "FIRST", "SECOND", "THIRD")
                        positions we should send to the betOfferLabel function
                        the betoffer with placeLimit 2 and for 3 non-"ANY"
                        selections we should send the betoffer with placeLimit
                        3.
                    </i>
                </p>
                <div>
                    <i>
                        You can also pass the{' '}
                        <b>multiPositionBetOfferLabelOneLine</b> parameter - it
                        is available only for Forecast/Tricast bets and decides
                        if:
                        <ul>
                            <li>
                                The participants for will be displayed in one
                                line with parenthesis separated by comma (set it
                                on true), eg: (1st) ParticipantName, (2nd)
                                ParticipantName
                            </li>
                            <li>
                                The participants for will be displayed on
                                separated lines (set it on false) eg: <br />
                                1st ParticipantName <br />
                                2nd ParticipantName
                            </li>
                        </ul>
                    </i>
                </div>
                <p>
                    <i>
                        For other type of bets as Reverse Forecast or
                        Combination Forecast, the above{' '}
                        <b>multiPositionBetOfferLabelOneLine</b> isn't
                        available, and by default the participants will be
                        displayed in one line.
                    </i>
                </p>
                <p style={{ marginBottom: '0' }}>
                    <i>
                        It accepts a callback for translations, used to
                        translate some static text and the following
                        <b> keys needs to be added</b> to your own translations:
                    </i>
                </p>
                <ul>
                    <li>
                        <i>"betoffer.multiPosition.labels.1st": "1st"</i>
                    </li>
                    <li>
                        <i>"betoffer.multiPosition.labels.2nd": "2nd"</i>
                    </li>
                    <li>
                        <i>"betoffer.multiPosition.labels.3rd": "3rd"</i>
                    </li>
                </ul>
                <p>
                    <i>
                        If the callback isn't provided it will fallback to the
                        english text.
                    </i>
                </p>
                <CodeBlock
                    title={BetOfferMultiPosition.title}
                    expectedOutput={BetOfferMultiPosition.expectedOutput}
                    initialDisplayedCode={
                        BetOfferMultiPosition.initialDisplayedCode
                    }
                    moreCode={BetOfferMultiPosition.moreCode}
                />
            </TabPanel>
            <TabPanel index={4} value={value} className={classes.TabPanel}>
                <p>
                    If the betOffer doesn't have any of the types detailed in
                    the other examples, the default label is actually the
                    criterion.label received on the betOffer from the API.
                </p>
                <CodeBlock
                    title={BetOfferDefault.title}
                    expectedOutput={BetOfferDefault.expectedOutput}
                    initialDisplayedCode={BetOfferDefault.initialDisplayedCode}
                    moreCode={BetOfferDefault.moreCode}
                />
            </TabPanel>
        </div>
    );
};

export default BetOfferLabelTabs;
